import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { Field, Formik } from "formik";
import * as formsService from "../../../services/formsService";
import OnSubmitValidationError from "../../../components/FormComponents/OnSubmitValidationError";
import { CTA, InputField } from "@coverme/forms";
import { DateInput } from "../../../components/FormComponents/DateInput";
import { SimpleSingleSelect } from "../../../components/FormComponents/SimpleSingleSelect";
import { YesNoButtons } from "../../../components/FormComponents/YesNoButtons";
import { getAge, getCustomFormattedNameFromObject } from "../../../services/stringService";
import { createHouseholdMemberValidationSchema } from "./validationSchema";
import * as interviewActions from "../../../actions/interviewActions";
import { first } from "lodash-es";
import PregnancyField from "../Components/PregnancyFields";

const HouseholdMember = ({
    personId,
    isEdit,
    genders,
    relationships,
    patient,
    householdMembers,
    responsibleParty,
    includeResponsibleParty,
    setHouseholdMember,
    match,
    history,
}) => {
    let initialValues = {};
    let isLoading = false;

    const currentHouseholdMember = useMemo(() => {
        if (personId == null) {
            return null;
        } else {
            return first(householdMembers.filter(x => x.personId === personId));
        }
    }, [personId]);

    const onFormSubmit = formData => {
        isLoading = true;
        setHouseholdMember(formData).then(res => {
            isLoading = false;
            history.goBack();
        });
    };

    const defaultInitialValues = {
        livesWithPatient: null,
        relationshipToPatient: "",
        livesWithResponsibleParty: undefined,
        relationshipToResponsibleParty: null,
        dateOfBirth: "",
        firstName: "",
        gender: null,
        middleInitial: "",
        lastName: "",
        suffix: "",
        pregnancy: {
            isPregnant: null,
            expectedChildren: null,
            expectedDueDate: null,
        },
    };

    if (!isEdit) {
        initialValues = defaultInitialValues;
    } else {
        initialValues = { ...currentHouseholdMember };
    }

    const validationSchema = useCallback(createHouseholdMemberValidationSchema(includeResponsibleParty), [
        includeResponsibleParty,
    ]);

    function navigateToPreviousPage() {
        history.goBack();
    }

    return (
        <div className="wizard-page">
            <h2 className="title"> {isEdit ? "Edit" : "Add"} Household Member</h2>
            <Formik
                onSubmit={onFormSubmit}
                initialValues={initialValues}
                validationSchema={validationSchema}
                render={props => {
                    const { setFieldValue, setFieldTouched, resetForm, handleSubmit, errors, touched, values } = props;
                    const getValidationState = formsService.getValidationState.bind(this, errors, touched);
                    const getErrors = formsService.getErrors.bind(this, errors);
                    const isRequired = formsService.isRequired.bind(this, validationSchema, values);
                    return (
                        <form className="wizard-form">
                            <OnSubmitValidationError callback={() => {}} />
                            <div className="form-section">
                                <div className="form-control m-input">
                                    <Field
                                        name={`firstName`}
                                        render={({ field }) => (
                                            <InputField
                                                title={"First Name"}
                                                id={`firstName`}
                                                placeholder={"First Name"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control xs-input">
                                    <Field
                                        name={`middleInitial`}
                                        render={({ field }) => (
                                            <InputField
                                                title={"MI"}
                                                id={`middleInitial`}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                maxLength={1}
                                                required={isRequired(field)}
                                                {...field}
                                                placeholder={"MI"}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control m-input">
                                    <Field
                                        name={`lastName`}
                                        render={({ field }) => (
                                            <InputField
                                                title={"Last"}
                                                id={`lastName`}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                                placeholder={"Last Name"}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control s-input">
                                    <Field
                                        name={`suffix`}
                                        render={({ field }) => (
                                            <InputField
                                                title={"Suffix"}
                                                id={`suffix`}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                maxLength={5}
                                                {...field}
                                                placeholder={"Suffix"}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="form-control s-160-input">
                                <Field
                                    name="dateOfBirth"
                                    render={({ field }) => (
                                        <DateInput
                                            {...field}
                                            id={field.name}
                                            title={"Date of Birth"}
                                            changeEvent={v => setFieldValue(field.name, v)}
                                            errorMessage={getErrors(field)}
                                            required={isRequired(field)}
                                            validState={getValidationState(field)}
                                        />
                                    )}
                                />
                            </div>
                            <div className="form-control s-input">
                                <Field
                                    name="gender"
                                    render={({ field }) => (
                                        <SimpleSingleSelect
                                            {...field}
                                            id={field.name}
                                            title={"Gender"}
                                            setFieldTouched={setFieldTouched}
                                            setFieldValue={setFieldValue}
                                            errorMessage={getErrors(field)}
                                            validState={getValidationState(field)}
                                            required={isRequired(field)}
                                            options={genders}
                                            matchFromStart={true}
                                        />
                                    )}
                                />
                            </div>
                            {getAge(values.dateOfBirth) > 13 && values.gender === "Female" && (
                                <PregnancyField
                                    fullName={getCustomFormattedNameFromObject(values, "this person")}
                                    isRequired={isRequired}
                                    getErrors={getErrors}
                                    getValidationState={getValidationState}
                                />
                            )}
                            <div className="form-section">
                                <div className="form-control auto-input">
                                    <YesNoButtons
                                        {...props}
                                        name="livesWithPatient"
                                        title={`Does
                                        ${getCustomFormattedNameFromObject(values, "this person")} 
                                        live with ${patient.fullName}`}
                                        getErrors={getErrors}
                                        isRequired={isRequired}
                                        getValidationState={getValidationState}
                                    />
                                </div>
                                <div className="form-control m-input">
                                    <Field
                                        name="relationshipToPatient"
                                        render={({ field }) => (
                                            <SimpleSingleSelect
                                                {...field}
                                                id={field.name}
                                                title={`Relation to ${patient.fullName}`}
                                                setFieldTouched={setFieldTouched}
                                                setFieldValue={setFieldValue}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                options={relationships}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            {includeResponsibleParty && (
                                <div className="form-section">
                                    <div className="form-control auto-input">
                                        <YesNoButtons
                                            {...props}
                                            name="livesWithResponsibleParty"
                                            isRequired={isRequired}
                                            title={`Does
                                        ${getCustomFormattedNameFromObject(values, "this person")} 
                                        live with ${responsibleParty.fullName}`}
                                            getErrors={getErrors}
                                            getValidationState={getValidationState}
                                        />
                                    </div>
                                    <div className="form-control m-input">
                                        <Field
                                            name="relationshipToResponsibleParty"
                                            render={({ field }) => (
                                                <SimpleSingleSelect
                                                    {...field}
                                                    id={field.name}
                                                    title={`Relation to ${responsibleParty.fullName}`}
                                                    setFieldTouched={setFieldTouched}
                                                    required={isRequired(field)}
                                                    setFieldValue={setFieldValue}
                                                    errorMessage={getErrors(field)}
                                                    validState={getValidationState(field)}
                                                    options={relationships}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="wizard-buttons">
                                <CTA
                                    size={"large"}
                                    ctaType={"button"}
                                    styleType={"grey-dark-alt"}
                                    click={navigateToPreviousPage}
                                >
                                    &laquo; Cancel
                                </CTA>
                                <CTA
                                    size={"large"}
                                    ctaType={"submit"}
                                    styleType={"blue"}
                                    click={handleSubmit}
                                    loading={isLoading}
                                >
                                    Submit &raquo;
                                </CTA>
                            </div>
                        </form>
                    );
                }}
            />
            <div className="required_field_indicator_explained">Indicates required field</div>
        </div>
    );
};

export const AddHouseholdMemberContainer = connect(
    (state, ownProps) => ({
        isEdit: false,
        personId: undefined,
        genders: state.interviewLookups.data.gender || [],
        relationships: state.interviewLookups.data.relationships,
        patient: state.interview.entry.patient,
        householdMembers: state.interview.householdMembers,
        responsibleParty: state.interview.entry.responsibleParty,
        includeResponsibleParty: state.interview.entry.isResponsiblePartyDifferentFromPatient,
    }),
    dispatch => ({
        setHouseholdMember: data => dispatch(interviewActions.addHouseholdMember(data)),
    })
)(HouseholdMember);

export const EditHouseholdMemberContainer = connect(
    (state, ownProps) => ({
        isEdit: true,
        personId: ownProps.match.params.personId,
        genders: state.interviewLookups.data.gender || [],
        relationships: state.interviewLookups.data.relationships,
        patient: state.interview.entry.patient,
        householdMembers: state.interview.householdMembers,
        responsibleParty: state.interview.entry.responsibleParty,
        includeResponsibleParty: state.interview.entry.isResponsiblePartyDifferentFromPatient,
    }),
    dispatch => ({
        setHouseholdMember: data => dispatch(interviewActions.updateHouseholdMember(data)),
    })
)(HouseholdMember);
